import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MaskedLine, FadeIn, Chapter, SpotlightImage } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { BUYER_FORM_URL, VENDOR_FORM_URL } from "@/constants/links";
import { categories } from "@/constants/categories";
import { services } from "@/constants/content";

const HERO_IMG = "https://images.unsplash.com/photo-1771072427512-74602f4fb49a?q=85&w=1400&auto=format&fit=crop";
const VISION_IMG = "https://images.unsplash.com/photo-1788579451438-f5d1143109c8?q=85&w=1400&auto=format&fit=crop";

const benefits = [
  { n: "01", title: "Curated, not crowded", copy: "Every product and every vendor is selected with intent — quality you can feel, not a feed you have to fight." },
  { n: "02", title: "Cross-border, simplified", copy: "Pricing, duties and delivery handled in one transparent flow. No middlemen, no guesswork, no surprises." },
  { n: "03", title: "Trust by design", copy: "Vendor verification and buyer protection are being built into the foundation — not bolted on later." },
  { n: "04", title: "Built in Africa, open to the world", copy: "Engineered by a Nigerian team, connected everywhere. Local understanding, global reach." },
];

const steps = [
  { n: "01", title: "User Registration", copy: "Buyers, vendors, service providers, and dispatch riders register and verify their accounts on the AFLIMART platform." },
  { n: "02", title: "Product & Service Listing", copy: "Vendors and service providers list their products and services, set prices, and manage availability through their dashboards." },
  { n: "03", title: "Order Placement", copy: "Customers browse, select products or services, and place orders securely through the platform." },
  { n: "04", title: "Payment & Confirmation", copy: "Payments are processed securely, and orders are confirmed instantly for vendors and logistics partners." },
  { n: "05", title: "Dispatch & Delivery", copy: "Verified dispatch riders and logistics partners pick up and deliver orders efficiently." },
  { n: "06", title: "Completion & Feedback", copy: "Orders are completed, and users can rate vendors, riders, and services to maintain quality and trust." },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -90]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden pb-20 pt-32 sm:pt-40 lg:pb-28 lg:pt-44">
      <motion.div style={{ y: glowY }} className="pointer-events-none absolute -top-32 right-[-10%] h-[34rem] w-[34rem] rounded-full bg-[#1E3A8A]/15 blur-[120px]" aria-hidden="true" />
      <div className="pointer-events-none absolute bottom-0 left-[-12%] h-[26rem] w-[26rem] rounded-full bg-[#1E3A8A]/5 blur-[100px]" aria-hidden="true" />
      {[
        { top: "18%", left: "6%", size: 6, delay: "0s" },
        { top: "32%", left: "46%", size: 4, delay: "1.4s" },
        { top: "12%", left: "70%", size: 5, delay: "2.6s" },
        { top: "64%", left: "3%", size: 4, delay: "3.4s" },
        { top: "72%", left: "52%", size: 6, delay: "0.9s" },
      ].map((p, i) => (
        <span
          key={i}
          aria-hidden="true"
          className="animate-float-slow pointer-events-none absolute rounded-full bg-[#1E3A8A]/60 blur-[1px]"
          style={{ top: p.top, left: p.left, width: p.size, height: p.size, animationDelay: p.delay }}
        />
      ))}

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-4 sm:px-8 lg:grid-cols-12 lg:px-16">
        <div className="lg:col-span-7">
          <motion.p
            data-testid="hero-eyebrow"
            className="eyebrow flex items-center gap-3"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#1E3A8A]" />
            Aflimart Digital Market · Nigeria · Pre-Launch
          </motion.p>

          <h1 data-testid="hero-headline" className="mt-8 font-sans text-5xl font-bold leading-[1.04] tracking-tight text-[#0F172A] sm:text-7xl lg:text-[5.75rem]">
            <MaskedLine delay={0.15}>SHOP THE</MaskedLine>
            <MaskedLine delay={0.3} className="text-gold-gradient">WORLD FROM</MaskedLine>
            <MaskedLine delay={0.45}>HOME<span className="text-[#1E3A8A]">.</span></MaskedLine>
          </h1>

          <FadeIn delay={0.7}>
            <p data-testid="hero-subcopy" className="mt-8 max-w-xl text-base leading-relaxed text-[#64748B] sm:text-lg">
              Aflimart Digital Market is building a borderless marketplace — connecting African
              shoppers to the world's finest products, and African makers to the world. The doors
              are not open yet. Something extraordinary is being built.
            </p>
          </FadeIn>

          <FadeIn delay={0.85} className="mt-10 flex flex-wrap items-center gap-4">
            <a href={BUYER_FORM_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-buyer" className="btn-gold">
              Join the Buyer Waitlist <ArrowUpRight size={16} />
            </a>
            <a href={VENDOR_FORM_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-vendor" className="btn-ghost">
              Become a Vendor
            </a>
          </FadeIn>
        </div>

        <motion.div style={{ y: imgY }} className="relative lg:col-span-5" data-testid="hero-visual">
          <div className="absolute -inset-6 rounded-full bg-[#1E3A8A]/10 blur-3xl animate-pulse-glow" aria-hidden="true" />
          <FadeIn delay={0.5} y={60}>
            <div className="relative overflow-hidden rounded-2xl border border-[#1E3A8A]/25 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.8)]">
              <img
                src={HERO_IMG}
                alt="Editorial luxury commerce portrait — woman with designer handbag in warm amber interior"
                data-testid="hero-image"
                className="aspect-[4/5] w-full object-cover grayscale-[35%] sepia-[18%] contrast-[1.05]"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#08080A]/80 via-transparent to-[#08080A]/10" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
                <span className="font-sans text-[10px] tracking-[0.3em] text-[#1E3A8A]/90">EST. NIGERIA</span>
                <span className="h-1.5 w-1.5 rotate-45 bg-[#1E3A8A]" />
              </div>
            </div>
          </FadeIn>
        </motion.div>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee />

      {/* 01 — The Vision */}
      <section data-testid="chapter-01-vision" className="mx-auto max-w-7xl px-4 py-24 sm:px-8 sm:py-32 lg:px-16">
        <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Chapter index="01" eyebrow="About Aflimart" title="A marketplace without borders." />
            <FadeIn delay={0.25}>
              <p className="mt-8 max-w-xl text-base leading-relaxed text-[#64748B] sm:text-lg">
                Aflimart Digital Market — a product of Aflimart Technologies LTD, a Nigerian
                technology company — is built on one conviction: geography should never decide what
                you can own. We are creating a single platform where
                a shopper in Lagos reaches the same shelves as a shopper in London — and where a maker
                in Kano sells to the world.
              </p>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-[#64748B] sm:text-lg">
                Curated with intent. Delivered with trust. This is commerce, reimagined from Africa outward.
              </p>
              <Link to="/about" data-testid="vision-link-about" className="group mt-8 inline-flex items-center gap-2 font-sans text-sm font-medium text-[#1E3A8A] transition-colors hover:text-[#1D4ED8]">
                Read our story
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </FadeIn>
          </div>
          <FadeIn delay={0.2} className="lg:col-span-5">
            <SpotlightImage src={VISION_IMG} alt="Editorial portrait in dramatic sculpted light — global fashion confidence" testId="vision-image" />
          </FadeIn>
        </div>
      </section>


      {/* 02 — Why Aflimart */}
      <section data-testid="chapter-04-benefits" className="border-t border-[#1E3A8A]/10 bg-[#F2F4F7]">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 sm:py-32 lg:px-16">
          <Chapter index="02" eyebrow="Why Aflimart" title="What we're building toward." />
          <div className="mt-14">
            {benefits.map((b, i) => (
              <FadeIn key={b.n} delay={0.07 * i}>
                <div data-testid={`benefit-row-${i + 1}`} className="group grid grid-cols-1 gap-4 border-t border-[#E2E8F0] py-8 transition-colors duration-500 hover:border-[#1E3A8A]/30 sm:grid-cols-12 sm:items-baseline sm:gap-8">
                  <span className="font-sans text-sm tracking-[0.25em] text-[#1E3A8A] sm:col-span-2">{b.n}</span>
                  <h3 className="font-sans text-xl text-[#0F172A] transition-colors duration-500 group-hover:text-[#1D4ED8] sm:col-span-4 sm:text-2xl">{b.title}</h3>
                  <p className="text-sm leading-relaxed text-[#64748B] sm:col-span-6 sm:text-base">{b.copy}</p>
                </div>
              </FadeIn>
            ))}
            <div className="border-t border-[#E2E8F0]" />
          </div>
        </div>
      </section>

      {/* 03 — Planned Categories */}
      <section id="marketplace" data-testid="chapter-03-categories" className="mx-auto max-w-7xl px-4 py-24 sm:px-8 sm:py-32 lg:px-16">
        <Chapter index="03" eyebrow="Planned Categories" title="What the shelves will hold." />
        <FadeIn delay={0.2}>
          <p className="mt-6 max-w-xl text-sm text-[#94A3B8] sm:text-base">
            Aflimart Digital Market will open with these five launch categories — each one deliberately
            chosen, never crowded. Additional categories may be introduced as Aflimart scales and expands.
          </p>
        </FadeIn>
        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-12 lg:gap-8">
          {categories.map((c, i) => (
            <FadeIn key={c.name} delay={0.06 * i} className={c.span}>
              <Link to={`/categories/${c.slug}`} data-testid={`category-card-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="card-dark group block h-full overflow-hidden hover:-translate-y-1">
                <div className="relative h-44 overflow-hidden sm:h-52">
                  <img
                    src={c.img}
                    alt={c.alt}
                    data-testid={`category-image-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="h-full w-full object-cover grayscale-[30%] sepia-[15%] contrast-[1.05] transition-transform duration-700 group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0F172A] via-transparent to-transparent" />
                  <span className="absolute right-5 top-5 font-sans text-[10px] tracking-[0.25em] text-[#64748B]">PLANNED FOR LAUNCH</span>
                </div>
                <div className="p-8 sm:p-10">
                  <span className="flex h-12 w-12 rotate-45 items-center justify-center rounded-lg border border-[#1E3A8A]/40 transition-colors duration-500 group-hover:border-[#1E3A8A] group-hover:bg-[#1E3A8A]/10">
                    <c.icon size={20} className="-rotate-45 text-[#1E3A8A]" />
                  </span>
                  <h3 className="mt-8 font-sans text-xl text-[#0F172A] sm:text-2xl">{c.name}</h3>
                  <p className="mt-3 max-w-md text-sm leading-relaxed text-[#64748B]">{c.copy}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#1E3A8A]">
                    Preview this category
                    <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </span>
                </div>
              </Link>
            </FadeIn>
          ))}
        </div>
      </section>


      {/* 04 — How It Works Preview */}
      <section data-testid="chapter-05-process" className="mx-auto max-w-7xl px-4 py-24 sm:px-8 sm:py-32 lg:px-16">
        <Chapter index="04" eyebrow="How It Works" title="How AFLIMART Works" />
        <div className="mt-14 grid grid-cols-1 gap-x-10 gap-y-6 sm:grid-cols-2 lg:grid-cols-3">
          {steps.map((s, i) => (
            <FadeIn key={s.n} delay={0.06 * i}>
              <div data-testid={`process-step-${i + 1}`} className="flex items-baseline gap-4 border-b border-[#E2E8F0] pb-5">
                <span className="font-sans text-sm tracking-[0.25em] text-[#1E3A8A]">{s.n}</span>
                <span className="font-sans text-lg text-[#0F172A] sm:text-xl">{s.title}</span>
              </div>
            </FadeIn>
          ))}
        </div>
        <FadeIn delay={0.2}>
          <Link to="/how-it-works" data-testid="how-preview-link" className="group mt-10 inline-flex items-center gap-2 font-sans text-sm font-medium text-[#1E3A8A] transition-colors hover:text-[#1D4ED8]">
            See the full six steps
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        </FadeIn>
      </section>

      {/* 05 — Services Preview */}
      <section data-testid="chapter-05-services" className="border-t border-[#1E3A8A]/10 bg-[#F2F4F7]">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 sm:py-32 lg:px-16">
          <Chapter index="05" eyebrow="Services" title="What Aflimart will do for you." />
          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
            {services.map((s, i) => (
              <FadeIn key={s.name} delay={0.06 * i}>
                <div data-testid={`service-preview-${i + 1}`} className="card-dark group h-full p-7 hover:-translate-y-1 sm:p-8">
                  <span className="font-sans text-xs tracking-[0.25em] text-[#1E3A8A]/50">0{i + 1}</span>
                  <h3 className="mt-4 font-sans text-lg text-[#0F172A] transition-colors duration-500 group-hover:text-[#1D4ED8]">{s.name}</h3>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.2}>
            <Link to="/services" data-testid="services-preview-link" className="group mt-10 inline-flex items-center gap-2 font-sans text-sm font-medium text-[#1E3A8A] transition-colors hover:text-[#1D4ED8]">
              Explore all four services
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* 06 — Vision & Mission Preview */}
      <section data-testid="chapter-06-vision-mission" className="mx-auto max-w-7xl px-4 py-24 sm:px-8 sm:py-32 lg:px-16">
        <Chapter index="06" eyebrow="Vision & Mission" title="Why we exist." />
        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
          <FadeIn>
            <div className="card-dark h-full p-8 sm:p-10" data-testid="vision-preview-card">
              <span className="eyebrow">Our Vision</span>
              <p className="mt-6 font-sans text-lg leading-relaxed text-[#0F172A] sm:text-xl">
                To become Africa’s most trusted and inclusive digital marketplace, empowering individuals and businesses to trade, grow, and thrive through innovative technology and seamless commerce solutions.
              </p>
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="card-dark h-full p-8 sm:p-10" data-testid="mission-preview-card">
              <span className="eyebrow">Our Mission</span>
              <p className="mt-6 font-sans text-lg leading-relaxed text-[#0F172A] sm:text-xl">
                Four commitments — to buyers, vendors, service providers and logistics partners — on one secure platform.
              </p>
              <Link to="/about" data-testid="vm-preview-link" className="group mt-8 inline-flex items-center gap-2 font-sans text-sm font-medium text-[#1E3A8A] transition-colors hover:text-[#1D4ED8]">
                Read our vision & mission
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 07 — Early Access */}
      <section data-testid="chapter-06-access" className="relative overflow-hidden border-t border-[#1E3A8A]/10">
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[30rem] w-[50rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#1E3A8A]/10 blur-[120px]" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-8 sm:py-36 lg:px-16">
          <Chapter index="07" eyebrow="Early Access" title="The doors open soon." />
          <FadeIn delay={0.25}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-[#64748B] sm:text-lg">
              Aflimart Digital Market is in pre-launch. Waitlist members and registered vendors will be the first
              through the doors — with priority access and a first look at the collections.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href={BUYER_FORM_URL} target="_blank" rel="noopener noreferrer" data-testid="access-cta-buyer" className="btn-gold">
                Join the Buyer Waitlist <ArrowUpRight size={16} />
              </a>
              <a href={VENDOR_FORM_URL} target="_blank" rel="noopener noreferrer" data-testid="access-cta-vendor" className="btn-ghost">
                Register as a Vendor
              </a>
            </div>
          </FadeIn>
        </div>
      </section>
    </>
  );
}
